import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trophy, Star, History, ChevronDown, Lock, X, KeyRound, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { StarField } from "./Decorations";
import { GROUPS, PANITIA_PASSWORD } from "../data/groups";

const API = "/api";

export const WeeklyAwards = () => {
  const [awards, setAwards] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [isPanitia, setIsPanitia] = useState(false);
  const [week, setWeek] = useState("");
  const [team, setTeam] = useState(GROUPS[0].codename);
  const [mvp, setMvp] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchAwards = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/awards`);
      setAwards(data);
    } catch {
      // awards gagal dimuat; coba lagi di polling berikutnya
    }
  }, []);

  useEffect(() => {
    fetchAwards();
    const id = setInterval(fetchAwards, 15000);
    return () => clearInterval(id);
  }, [fetchAwards]);

  const current = awards.length ? awards[awards.length - 1] : null;
  const history = awards.slice(0, -1).reverse();

  const submitPw = (e) => {
    e.preventDefault();
    if (pw.trim() === PANITIA_PASSWORD) {
      setIsPanitia(true);
      setShowPw(false);
      setPw("");
      setPwError(false);
    } else {
      setPwError(true);
      setTimeout(() => setPwError(false), 2500);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!week || !mvp.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/awards`, {
        password: PANITIA_PASSWORD,
        week: Number(week),
        team,
        mvp: mvp.trim(),
      });
      toast.success(`Team & MVP Minggu ${week} tersimpan!`);
      setWeek("");
      setMvp("");
      await fetchAwards();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan data.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      id="team-mvp"
      data-testid="weekly-awards-section"
      className="relative overflow-hidden bg-gradient-to-b from-[#191345] via-[#1a1145] to-[#191345] px-6 py-24 sm:py-32"
    >
      <StarField count={40} />

      {/* Akses panitia */}
      <div className="absolute right-4 top-4 z-20">
        {!isPanitia && (
          <button
            onClick={() => setShowPw((v) => !v)}
            data-testid="awards-panitia-button"
            aria-label="Akses panitia"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/40 transition-colors duration-300 hover:border-white/30 hover:text-white/80"
          >
            <Lock className="h-4 w-4" />
          </button>
        )}

        <AnimatePresence>
          {showPw && !isPanitia && (
            <motion.form
              onSubmit={submitPw}
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.2 }}
              data-testid="awards-panitia-form"
              className="glass absolute right-0 mt-2 w-64 rounded-2xl p-4 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-white/70">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#4cc9f0]" /> Akses Panitia
                </span>
                <button type="button" onClick={() => setShowPw(false)} aria-label="Tutup" className="text-white/40 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="relative mt-3">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Password panitia"
                  autoFocus
                  data-testid="awards-panitia-input"
                  className={`w-full rounded-full border bg-white/10 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0] ${
                    pwError ? "border-[#f72585]" : "border-white/20"
                  }`}
                />
              </div>
              <button
                type="submit"
                data-testid="awards-panitia-submit"
                className="mt-3 w-full rounded-full bg-[#4cc9f0] py-2.5 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.02]"
              >
                Masuk
              </button>
              <AnimatePresence>
                {pwError && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="awards-panitia-error"
                    className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#f72585]"
                  >
                    <X className="h-3.5 w-3.5" /> Password salah.
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.form>
          )}
        </AnimatePresence>

        {isPanitia && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="awards-editor-form"
            className="glass absolute right-0 w-72 rounded-2xl p-4 shadow-2xl"
          >
            <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-white/70">
              <ShieldCheck className="h-3.5 w-3.5 text-[#4cc9f0]" /> Input Minggu Baru
            </span>
            <input
              type="number"
              min="1"
              max="8"
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              placeholder="Minggu ke- (1-8)"
              data-testid="awards-week-input"
              className="mt-3 w-full rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0]"
            />
            <select
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              data-testid="awards-team-select"
              className="mt-2 w-full rounded-full border border-white/20 bg-[#1a1145] px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#4cc9f0]"
            >
              {GROUPS.map((g) => (
                <option key={g.codename} value={g.codename}>
                  {g.codename}
                </option>
              ))}
            </select>
            <input
              value={mvp}
              onChange={(e) => setMvp(e.target.value)}
              placeholder="Nama MVP"
              data-testid="awards-mvp-input"
              className="mt-2 w-full rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0]"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="awards-save-button"
              className="mt-3 w-full rounded-full bg-[#4cc9f0] py-2.5 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {saving ? "Menyimpan..." : "Simpan"}
            </button>
          </motion.form>
        )}
      </div>

      <div className="relative z-10 mx-auto max-w-6xl">
        <Reveal>
          <p className="mb-4 text-center text-sm font-medium uppercase tracking-[0.3em] text-[#f72585]">Apresiasi</p>
          <h2 className="font-heading text-center text-3xl font-medium leading-tight tracking-tight sm:text-4xl md:text-5xl">
            Team & <span className="aurora-text">MVP of the Week</span>
          </h2>
        </Reveal>

        {current ? (
          <Reveal delay={0.1}>
            <div className="relative mx-auto mt-12 max-w-3xl">
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#f72585] opacity-25 blur-[100px]" />
              <div className="rounded-[2rem] bg-gradient-to-r from-[#4cc9f0] via-[#7209b7] to-[#f72585] p-[1.5px]">
                <div
                  className="relative overflow-hidden rounded-[2rem] bg-[#120e33]/90 px-6 py-10 text-center backdrop-blur"
                  data-testid="awards-current-card"
                >
                  <StarField count={20} />
                  <span
                    data-testid="awards-week-badge"
                    className="relative inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs uppercase tracking-widest text-white/80"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-[#4cc9f0]" /> Minggu {current.week}
                  </span>
                  <div className="relative mt-8 grid gap-8 sm:grid-cols-2">
                    <div>
                      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#4cc9f0] to-[#4361ee] shadow-[0_0_30px_rgba(76,201,240,0.5)]">
                        <Trophy className="h-7 w-7 text-white" />
                      </span>
                      <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-white/60">Team of the Week</p>
                      <p data-testid="awards-current-team" className="font-heading mt-1 text-2xl font-semibold aurora-text sm:text-3xl">
                        {current.team}
                      </p>
                    </div>
                    <div>
                      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#f72585] to-[#b5179e] shadow-[0_0_30px_rgba(247,37,133,0.5)]">
                        <Star className="h-7 w-7 text-white" />
                      </span>
                      <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-white/60">MVP of the Week</p>
                      <p data-testid="awards-current-mvp" className="font-heading mt-1 text-2xl font-semibold text-white sm:text-3xl">
                        {current.mvp}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ) : (
          <p data-testid="awards-empty" className="mt-12 text-center text-sm text-white/50">
            Belum ada data — panitia akan mengumumkan sebentar lagi.
          </p>
        )}

        {/* Riwayat */}
        <Reveal delay={0.15}>
          <div className="mx-auto mt-10 max-w-3xl">
            <button
              onClick={() => setShowHistory((v) => !v)}
              data-testid="awards-history-toggle"
              className="mx-auto flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-6 py-3 font-heading text-sm font-medium text-white/85 transition-colors duration-300 hover:border-[#4cc9f0]/50 hover:text-white"
            >
              <History className="h-4 w-4 text-[#4cc9f0]" />
              Riwayat
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${showHistory ? "rotate-180" : ""}`}
              />
            </button>

            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-2" data-testid="awards-history-list">
                    {history.length === 0 && (
                      <p className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 text-center text-sm text-white/50">
                        Belum ada riwayat minggu sebelumnya.
                      </p>
                    )}
                    {history.map((a) => (
                      <div
                        key={a.id}
                        data-testid={`awards-history-item-${a.week}`}
                        className="flex flex-col items-center justify-between gap-1 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 text-sm sm:flex-row"
                      >
                        <span className="font-heading font-medium text-white/90">Minggu {a.week}</span>
                        <span className="text-white/70">
                          Team: <span className="font-medium text-white">{a.team}</span>
                          <span className="mx-2 text-white/30">·</span>
                          MVP: <span className="font-medium text-white">{a.mvp}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
